import React, { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
    Calendar,
    Users,
    Send,
    Megaphone,
    Sparkles,
    Copy,
    Check,
    ExternalLink,
    ShieldCheck,
    ArrowRight,
    ArrowLeft,
} from 'lucide-react';
import { AnthropicIcon, GoogleGeminiIcon, GrokIcon, OpenAIIcon } from './llm-icons';
import { appConfig } from '@/common/config/app.config';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common/config/constants';
import { useRecordMcpEventMutation } from '@common-reducers/backend-api/backend-api';
import type { McpClientEventName } from '@common-reducers/backend-api/backend-api';
import {
    LLM_PLATFORM_META,
    noteKeysFor,
    STEP3_INSTRUCTION_COUNT,
    flowKeyPrefix,
    inlineStepKeys,
    MCP_INLINE_STEPS_ENABLED,
    isPlatformConnectable,
    isOneClickAddEnabled,
    oneClickEventProps,
    oneClickTarget,
    type LlmPlatformId,
} from './mcp-platforms';
import { GuideVideoButton } from '@/common/components/guide-video/guide-video-button';

// Available LLM platforms. Claude, ChatGPT, Gemini and Grok are live.
// Behaviour flags (comingSoon / flow / connectUrl) live in the React-free `mcp-platforms` module
// so they are unit-testable; this table only adds the presentation (icon + tile background).
const PLATFORM_PRESENTATION: Record<LlmPlatformId, { icon: IconComponent; iconBg: string }> = {
    claude: { icon: AnthropicIcon, iconBg: 'bg-[#f5f2ee] dark:bg-slate-800' },
    chatgpt: { icon: OpenAIIcon, iconBg: 'bg-slate-100 dark:bg-slate-800' },
    gemini: { icon: GoogleGeminiIcon, iconBg: 'bg-slate-100 dark:bg-slate-800' },
    grok: { icon: GrokIcon, iconBg: 'bg-slate-100 dark:bg-slate-800' },
};

type IconComponent = React.FC<{ className?: string; size?: number }>;

/** Platform → its Academy lesson. A lesson without a video renders nothing. */
const PLATFORM_LESSON: Record<string, string> = { claude: 'claude-connector', chatgpt: 'chatgpt-connector' };

const LLM_PLATFORMS = LLM_PLATFORM_META.map((meta) => ({ ...meta, ...PLATFORM_PRESENTATION[meta.id] }));

interface MCPConnectionDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

/** Staggered fade-up so the dialog assembles on open. */
const reveal = (i: number): React.CSSProperties => ({ animationDelay: `${i * 60}ms` });

// A soft brand-blue glow behind the header + the brand hairline are shared with
// the OAuth consent page (mcp-oauth-page.tsx) so the two connection surfaces
// read as one family.
const Shell: React.FC<{ children: React.ReactNode; maxW: string }> = ({ children, maxW }) => (
    <DialogContent
        className={`${maxW} max-h-[92vh] overflow-y-auto gap-0 rounded-2xl border-slate-200 bg-white p-0 dark:border-slate-800 dark:bg-slate-900`}
    >
        <div className="h-1 w-full shrink-0 bg-gradient-to-r from-[#53bdeb] to-[#259ae9]" />
        <div className="relative p-5 sm:p-6">
            <div
                aria-hidden
                className="pointer-events-none absolute -top-24 left-1/2 h-64 w-64 -translate-x-1/2 rounded-full blur-3xl"
                style={{ background: 'radial-gradient(circle, rgba(83,189,235,0.16), transparent 65%)' }}
            />
            <div className="relative">{children}</div>
        </div>
    </DialogContent>
);

// A brand-agnostic dashed connector line for the header motif.
const LinkDashes = () => (
    <svg className="h-4 w-8 text-[#53bdeb]/60" viewBox="0 0 32 16" fill="none" aria-hidden>
        <path d="M2 8h28" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeDasharray="4 4" />
    </svg>
);

const BlueticksChip = () => (
    <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-[#53bdeb]/30 bg-[#53bdeb]/10">
        <img src="/img/blueticks-btn-logo.svg" alt="Blueticks" className="h-6 w-auto" />
    </span>
);

export const MCPConnectionDialog: React.FC<MCPConnectionDialogProps> = ({ open, onOpenChange }) => {
    const { t } = useTranslation(namespace);
    const [selectedPlatform, setSelectedPlatform] = useState<string | null>(null);
    const [showInstructions, setShowInstructions] = useState(false);
    const [copied, setCopied] = useState(false);
    // #493 C one-click add: null until clicked, then whether the clipboard write succeeded.
    const [oneClickCopied, setOneClickCopied] = useState<boolean | null>(null);
    const [recordMcpEvent] = useRecordMcpEventMutation();
    const [firstPromptCopied, setFirstPromptCopied] = useState(false);
    const firstPromptTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Verified streamable-HTTP endpoint for the remote MCP connector (NOT /mcp/sse,
    // the deprecated SSE transport — claude.ai's by-URL custom connector uses
    // streamable HTTP, and /mcp is the OAuth-hardened path we validated end-to-end).
    const mcpServerUrl = `${appConfig.API_ENDPOINT}/mcp`;

    // Fire-and-forget: never block or break the dialog on event failure.
    const fireEvent = (name: McpClientEventName, props?: Record<string, unknown>) => {
        try {
            recordMcpEvent({ name, props }).catch(() => { /* intentionally ignored */ });
        } catch {
            // never break the dialog
        }
    };

    // Fire mcp_dialog_opened once per open transition (guard re-fire on re-render).
    const openedRef = useRef(false);
    useEffect(() => {
        if (open && !openedRef.current) {
            openedRef.current = true;
            fireEvent('mcp_dialog_opened');
        } else if (!open) {
            openedRef.current = false;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Track pending timeouts so they can be cleared on unmount (avoid setState-after-unmount)
    const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        return () => {
            if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
            if (firstPromptTimeoutRef.current) clearTimeout(firstPromptTimeoutRef.current);
            if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
        };
    }, []);

    const handleCopyUrl = () => {
        navigator.clipboard.writeText(mcpServerUrl);
        setCopied(true);
        if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
        copyTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
        fireEvent('mcp_url_copied', { platform: selectedPlatform });
    };

    // #1165 - a ready-made first prompt (read-only, works on a read-only grant) so the first
    // thing a new user does after approving is a call that returns something useful.
    const handleCopyFirstPrompt = () => {
        try {
            navigator.clipboard.writeText(t('aiAssistant.step5.prompt'))
                .then(() => {
                    setFirstPromptCopied(true);
                    if (firstPromptTimeoutRef.current) clearTimeout(firstPromptTimeoutRef.current);
                    firstPromptTimeoutRef.current = setTimeout(() => setFirstPromptCopied(false), 2000);
                })
                .catch(() => { /* copying is a convenience, the prompt stays visible */ });
        } catch {
            // never break the dialog
        }
    };

    const handleSelectPlatform = (platformId: string) => {
        setSelectedPlatform(platformId);
        setShowInstructions(true);
        fireEvent('mcp_platform_selected', { platform: platformId });
    };

    const handleClose = () => {
        onOpenChange(false);
        // Reset state after dialog closes
        if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = setTimeout(() => {
            setShowInstructions(false);
            setSelectedPlatform(null);
            setOneClickCopied(null);
        }, 300);
    };

    const handleBack = () => { setShowInstructions(false); setSelectedPlatform(null); setOneClickCopied(null); };

    // ── Instructions view ────────────────────────────────────────────────────
    if (showInstructions && selectedPlatform) {
        const platform = LLM_PLATFORMS.find((p) => p.id === selectedPlatform);
        const PlatformIcon = platform?.icon;
        const keys = flowKeyPrefix(platform?.flow);
        const oneClick = isOneClickAddEnabled(window.location.hostname) ? oneClickTarget(platform?.flow) : null;

        // One click = copy the URL + open the platform's add-connector screen. window.open runs first,
        // synchronously, so the popup keeps the click's user activation; the clipboard write follows.
        const handleOneClickAdd = () => {
            if (!oneClick || !platform) return;
            window.open(oneClick.openUrl, '_blank', 'noopener,noreferrer');
            const props = oneClickEventProps(platform.id);
            fireEvent('mcp_go_to_platform_clicked', props);
            try {
                navigator.clipboard
                    .writeText(mcpServerUrl)
                    .then(() => {
                        setOneClickCopied(true);
                        fireEvent('mcp_url_copied', props);
                    })
                    .catch(() => setOneClickCopied(false));
            } catch {
                setOneClickCopied(false);
            }
        };
        const steps = [
            {
                title: t('aiAssistant.step1.title'),
                body: (
                    <>
                        <p className="mb-2 text-xs text-slate-500 dark:text-slate-400">{t('aiAssistant.step1.label')}</p>
                        <div className="flex gap-2">
                            <div className="flex flex-1 items-center overflow-x-auto rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 font-mono text-[13px] text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
                                {mcpServerUrl}
                            </div>
                            <button
                                type="button"
                                onClick={handleCopyUrl}
                                aria-label="Copy MCP URL"
                                className={`flex h-[38px] shrink-0 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition-colors hover:bg-[#53bdeb]/10 hover:text-[#259ae9] dark:border-slate-700 dark:text-slate-400 ${MCP_INLINE_STEPS_ENABLED ? 'px-3' : 'w-[38px]'}`}
                            >
                                {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
                                {MCP_INLINE_STEPS_ENABLED && <span className="ml-1.5 text-xs font-medium">{t('aiAssistant.inline.copyButton')}</span>}
                            </button>
                        </div>
                        {MCP_INLINE_STEPS_ENABLED && (
                            <div className="mt-2.5 rounded-lg border border-[#53bdeb]/20 bg-[#53bdeb]/[0.05] px-3 py-2" data-testid="mcp-inline-steps">
                                <p className="mb-1 text-xs font-semibold text-[#1c86c4] dark:text-[#7fd0f2]">{t('aiAssistant.inline.title')}</p>
                                <ol className="space-y-0.5 text-[12.5px] text-slate-600 dark:text-slate-300">
                                    {inlineStepKeys(platform?.flow).map((k, n) => (
                                        <li key={k} className="flex gap-2">
                                            <span className="font-semibold text-[#53bdeb]">{n + 1}.</span>
                                            {t(k, { platform: platform?.name }) /* i18n-dynamic-key: inlineStepKeys() keys, checked in its own table */}
                                        </li>
                                    ))}
                                </ol>
                            </div>
                        )}
                    </>
                ),
            },
            {
                title: t(`${keys.step2}.title`),
                body: (
                    <button
                        type="button"
                        onClick={() => {
                            fireEvent('mcp_go_to_platform_clicked', { platform: selectedPlatform });
                            window.open(platform?.connectUrl, '_blank');
                        }}
                        className="flex w-full items-center justify-between rounded-lg border border-slate-200 px-3.5 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:border-[#53bdeb]/50 hover:bg-[#53bdeb]/[0.04] dark:border-slate-700 dark:text-slate-200"
                    >
                        <span>{t(`${keys.step2}.button`, { platform: platform?.name })}</span>
                        <ExternalLink className="h-4 w-4 text-slate-400" />
                    </button>
                ),
            },
            {
                title: t(`${keys.step3}.title`),
                body: (
                    <div className="rounded-lg border border-slate-100 bg-slate-50/70 px-3 py-2.5 dark:border-slate-800 dark:bg-slate-800/40">
                        <p className="mb-1.5 text-xs font-medium text-slate-600 dark:text-slate-300">
                            {t(`${keys.step3}.inPlatform`, { platform: platform?.name })}
                        </p>
                        <ol className="space-y-1 text-[13px] text-slate-600 dark:text-slate-300">
                            {Array.from({ length: STEP3_INSTRUCTION_COUNT }, (_, i) => i + 1).map((n) => (
                                <li key={n} className="flex gap-2">
                                    <span className="font-semibold text-[#53bdeb]">{n}.</span>
                                    {t(`${keys.step3}.instruction${n}`)}
                                </li>
                            ))}
                        </ol>
                    </div>
                ),
            },
            {
                title: t('aiAssistant.step4.title'),
                body: (
                    <div className="flex items-start gap-2.5 rounded-lg border border-[#53bdeb]/20 bg-[#53bdeb]/[0.06] px-3.5 py-3">
                        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#259ae9]" />
                        <p className="text-[13px] leading-relaxed text-[#1c86c4] dark:text-[#7fd0f2]">
                            {t('aiAssistant.step4.description', { platform: platform?.name })}
                        </p>
                    </div>
                ),
            },
            {
                title: t('aiAssistant.step5.title'),
                body: (
                    <div data-testid="mcp-first-prompt" className="flex gap-2">
                        <div className="flex flex-1 items-center rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[13px] text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
                            {t('aiAssistant.step5.prompt')}
                        </div>
                        <button type="button" onClick={handleCopyFirstPrompt} className="shrink-0 rounded-lg border border-slate-200 px-3 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800">
                            {firstPromptCopied ? t('aiAssistant.step5.copied') : t('aiAssistant.step5.copy')}
                        </button>
                    </div>
                ),
            },
        ];

        const capabilities = [
            { Icon: Send, label: t('aiAssistant.capabilities.message') },
            { Icon: Users, label: t('aiAssistant.capabilities.contacts') },
            { Icon: Megaphone, label: t('aiAssistant.capabilities.campaigns') },
        ];

        return (
            <Dialog open={open} onOpenChange={handleClose}>
                <Shell maxW="sm:max-w-[720px]">
                    {/* Header — platform ⟷ Blueticks connection motif */}
                    <div
                        className="animate-in fade-in duration-500 flex flex-col items-center text-center"
                        style={reveal(0)}
                    >
                        <div className="mb-3 flex items-center gap-2.5">
                            <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${platform?.iconBg}`}>
                                {PlatformIcon && <PlatformIcon size={22} />}
                            </span>
                            <LinkDashes />
                            <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-[#53bdeb]/30 bg-[#53bdeb]/10">
                                <img src="/img/blueticks-btn-logo.svg" alt="Blueticks" className="h-[22px] w-auto" />
                            </span>
                        </div>
                        <DialogTitle className="text-lg font-semibold leading-tight text-slate-900 dark:text-slate-50">
                            {t('aiAssistant.connectTitle', { platform: platform?.name })}
                        </DialogTitle>
                        <DialogDescription className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                            {t('aiAssistant.connectDescription', { platform: platform?.name })}
                            {/* The step-by-step for THIS platform (5.3 Claude; 5.4 ChatGPT lights up when published). */}
                            {PLATFORM_LESSON[selectedPlatform] && (
                                <GuideVideoButton lesson={PLATFORM_LESSON[selectedPlatform]} variant="link" surface={`mcp_dialog_${selectedPlatform}`} style={{ marginInlineStart: 8 }} />
                            )}
                        </DialogDescription>
                    </div>

                    {/* #493 C — one-click add (copy URL + open the add-connector screen); the steps stay as the manual path */}
                    {oneClick && platform && (
                        <div className="animate-in fade-in duration-500 mt-4" style={reveal(1)} data-testid="mcp-one-click">
                            <button
                                type="button"
                                onClick={handleOneClickAdd}
                                className="flex w-full items-center justify-center gap-2.5 rounded-xl bg-[#53bdeb] px-5 py-3 text-[15px] font-semibold text-white shadow-md shadow-[#53bdeb]/25 transition-all hover:-translate-y-0.5 hover:bg-[#259ae9] hover:shadow-lg active:translate-y-0"
                            >
                                {PlatformIcon && (
                                    <span className="flex h-6 w-6 items-center justify-center rounded-md bg-white text-slate-900">
                                        <PlatformIcon size={16} />
                                    </span>
                                )}
                                {t('aiAssistant.oneClick.button', { platform: platform.name })}
                                <ExternalLink className="h-4 w-4 opacity-80" />
                            </button>
                            {oneClickCopied === null ? (
                                <p className="mt-2 text-center text-xs text-slate-500 dark:text-slate-400">
                                    {t('aiAssistant.oneClick.subtitle', { platform: platform.name })}
                                </p>
                            ) : (
                                <div className="mt-2.5 flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50/80 px-3 py-2 dark:border-emerald-500/20 dark:bg-emerald-500/[0.06]" role="status">
                                    {oneClickCopied && <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />}
                                    <p className="text-[13px] leading-relaxed text-emerald-900 dark:text-emerald-200">
                                        {oneClickCopied && <span className="font-semibold">{t('aiAssistant.oneClick.copied')}. </span>}
                                        {t(oneClick.hintKey) /* i18n-dynamic-key: oneClickTarget() hint keys, checked in mcp-one-click.test */}
                                    </p>
                                </div>
                            )}
                            <div className="mt-4 flex items-center gap-3 text-xs font-medium uppercase tracking-wide text-slate-400 dark:text-slate-500">
                                <span className="h-px flex-1 bg-slate-200 dark:bg-slate-700" />
                                {t('aiAssistant.oneClick.manual')}
                                <span className="h-px flex-1 bg-slate-200 dark:bg-slate-700" />
                            </div>
                        </div>
                    )}

                    {/* Steps — 2-up grid (1,2 / 3,4) so the flow fits without an inner scrollbar */}
                    <div className="mt-4 grid gap-x-6 gap-y-3 sm:grid-cols-2 sm:items-start">
                        {steps.map((step, i) => (
                            <div
                                key={step.title}
                                className="animate-in fade-in slide-in-from-bottom-2 duration-500 flex gap-3.5"
                                style={reveal(i + 1)}
                            >
                                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#53bdeb] to-[#259ae9] text-xs font-bold text-white shadow-sm">
                                    {i + 1}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="mb-1.5 text-sm font-semibold text-slate-800 dark:text-slate-100">{step.title}</p>
                                    {step.body}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Platform-specific notes (ChatGPT: paid plan, menu names, Blueticks free limit) */}
                    {keys.notes && (
                        <div
                            className="animate-in fade-in duration-500 mt-4 rounded-lg border border-amber-200/70 bg-amber-50/70 px-3.5 py-2.5 dark:border-amber-500/20 dark:bg-amber-500/[0.06]"
                            style={reveal(steps.length + 1)}
                            data-testid="mcp-platform-notes"
                        >
                            <p className="mb-1 text-xs font-semibold text-amber-800 dark:text-amber-300">{t(`${keys.notes}.title`)}</p>
                            <ul className="list-disc space-y-0.5 pl-4 text-[13px] text-amber-900/90 dark:text-amber-200/90">
                                {noteKeysFor(platform?.flow).map((k) => (
                                    <li key={k}>{t(`${keys.notes}.${k}`)}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* What the assistant can do — compact inline row */}
                    <div
                        className="animate-in fade-in duration-500 mt-4 flex flex-wrap items-center gap-x-4 gap-y-1.5 border-t border-slate-100 pt-3.5 dark:border-slate-800"
                        style={reveal(steps.length + 1)}
                    >
                        <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
                            <Sparkles className="h-3.5 w-3.5 text-[#53bdeb]" />
                            {t('aiAssistant.capabilities.title', { platform: platform?.name })}
                        </span>
                        {capabilities.map(({ Icon, label }) => (
                            <span key={label} className="flex items-center gap-1.5 text-[13px] text-slate-600 dark:text-slate-300">
                                <Icon className="h-3.5 w-3.5 shrink-0 text-[#259ae9]" />
                                {label}
                            </span>
                        ))}
                    </div>

                    {/* Footer */}
                    <div className="mt-5 flex items-center justify-between gap-3">
                        <button
                            type="button"
                            onClick={handleBack}
                            className="inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium text-slate-500 transition-colors hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-100"
                        >
                            <ArrowLeft className="h-4 w-4" />
                            {t('aiAssistant.back')}
                        </button>
                        <button
                            type="button"
                            onClick={handleClose}
                            className="inline-flex items-center gap-2 rounded-md bg-[#53bdeb] px-5 py-2 text-sm font-medium text-white shadow-md shadow-[#53bdeb]/25 transition-all hover:-translate-y-0.5 hover:bg-[#259ae9] hover:shadow-lg active:translate-y-0"
                        >
                            <Check className="h-4 w-4" />
                            {t('aiAssistant.done')}
                        </button>
                    </div>
                </Shell>
            </Dialog>
        );
    }

    // ── Platform picker view ─────────────────────────────────────────────────
    const features = [
        { Icon: Calendar, title: t('aiAssistant.feature1.title'), example: t('aiAssistant.feature1.example') },
        { Icon: Users, title: t('aiAssistant.feature2.title'), example: t('aiAssistant.feature2.example') },
        { Icon: Sparkles, title: t('aiAssistant.feature3.title'), example: t('aiAssistant.feature3.example') },
    ];

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <Shell maxW="sm:max-w-[600px]">
                {/* Header — Blueticks ⟷ the AI trio */}
                <div className="animate-in fade-in duration-500 flex flex-col items-center text-center" style={reveal(0)}>
                    <div className="mb-3 flex items-center gap-2.5">
                        <BlueticksChip />
                        <LinkDashes />
                        <div className="flex items-center gap-1.5">
                            {LLM_PLATFORMS.map((p) => {
                                const Icon = p.icon;
                                return (
                                    <span key={p.id} className={`flex h-9 w-9 items-center justify-center rounded-lg ${p.iconBg} ${p.comingSoon ? 'grayscale' : ''}`}>
                                        <Icon size={20} />
                                    </span>
                                );
                            })}
                        </div>
                    </div>
                    <DialogTitle className="text-lg font-semibold leading-tight text-slate-900 dark:text-slate-50">
                        {t('aiAssistant.title')}
                    </DialogTitle>
                    <DialogDescription className="mt-1 text-sm leading-relaxed text-slate-500 dark:text-slate-400">
                        {t('aiAssistant.subtitle')}
                    </DialogDescription>
                </div>

                {/* Use-cases */}
                <div className="mt-5 grid gap-1.5">
                    {features.map(({ Icon, title, example }, i) => (
                        <div
                            key={title}
                            className="animate-in fade-in slide-in-from-bottom-2 duration-500 flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50/70 px-3.5 py-2 dark:border-slate-800 dark:bg-slate-800/40"
                            style={reveal(i + 1)}
                        >
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#53bdeb]/[0.12] text-[#259ae9]">
                                <Icon className="h-4 w-4" />
                            </div>
                            <div className="min-w-0">
                                <p className="text-sm font-medium text-slate-800 dark:text-slate-100">{title}</p>
                                <p className="truncate text-xs text-slate-500 dark:text-slate-400">"{example}"</p>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Platform picker */}
                <p className="animate-in fade-in duration-500 mt-5 mb-2.5 text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500" style={reveal(features.length + 1)}>
                    {t('aiAssistant.choosePlatform')}
                </p>
                <div className="grid gap-2.5">
                    {LLM_PLATFORMS.map((platform, i) => {
                        const Icon = platform.icon;
                        const disabled = !isPlatformConnectable(platform);
                        return (
                            <button
                                key={platform.id}
                                type="button"
                                disabled={disabled}
                                onClick={() => !disabled && handleSelectPlatform(platform.id)}
                                style={reveal(features.length + 2 + i)}
                                className={
                                    'animate-in fade-in slide-in-from-bottom-2 duration-500 group flex items-center gap-3.5 rounded-xl border-2 px-4 py-3 text-left transition-all ' +
                                    (disabled
                                        ? 'cursor-not-allowed border-slate-100 bg-slate-50/50 opacity-70 dark:border-slate-800 dark:bg-slate-800/30'
                                        : 'border-[#53bdeb]/40 bg-[#53bdeb]/[0.04] hover:-translate-y-0.5 hover:border-[#53bdeb] hover:shadow-lg hover:shadow-[#53bdeb]/10 dark:bg-[#53bdeb]/[0.06]')
                                }
                            >
                                <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${platform.iconBg} ${disabled ? 'grayscale' : ''}`}>
                                    <Icon size={24} />
                                </span>
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                        <span className={`text-[15px] font-semibold ${disabled ? 'text-slate-700 dark:text-slate-300' : 'text-slate-900 dark:text-slate-50'}`}>
                                            {platform.name}
                                        </span>
                                        {platform.popular && (
                                            <span className="rounded-full bg-[#53bdeb] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                                                {t('aiAssistant.popular')}
                                            </span>
                                        )}
                                        {platform.comingSoon && (
                                            <span className="rounded-full border border-slate-300 px-2 py-0.5 text-[10px] font-medium text-slate-500 dark:border-slate-600 dark:text-slate-400">
                                                {t('aiAssistant.comingSoon')}
                                            </span>
                                        )}
                                    </div>
                                    <p className={`mt-0.5 text-xs ${disabled ? 'text-slate-400 dark:text-slate-500' : 'text-slate-500 dark:text-slate-400'}`}>
                                        {disabled ? t('aiAssistant.integrationInDevelopment') : t('aiAssistant.connectViaMCP')}
                                    </p>
                                </div>
                                {!disabled && (
                                    <ArrowRight className="h-5 w-5 shrink-0 text-[#53bdeb] transition-transform group-hover:translate-x-0.5" />
                                )}
                            </button>
                        );
                    })}
                </div>

                {/* Footer note */}
                <p className="animate-in fade-in duration-500 mt-5 flex items-center justify-center gap-1.5 text-center text-xs text-slate-400 dark:text-slate-500" style={reveal(features.length + 2 + LLM_PLATFORMS.length)}>
                    <ShieldCheck className="h-3.5 w-3.5" />
                    {t('aiAssistant.footerInfo')}
                </p>
            </Shell>
        </Dialog>
    );
};
